import { getParamInputType } from './modelCalculationParams'
import type { ModelCalculationParamSelection, ModelParamDefinition } from '../types/modelParam'

export type ApplicantFormRow = Record<string, string | number | boolean>

export interface ModelTestFormValues {
  [key: string]: string | number | boolean | ApplicantFormRow[] | undefined
  applicants?: ApplicantFormRow[]
}

export function getDefaultTestFieldValue(
  field: string,
  definitions: ModelParamDefinition[],
  fallback?: string | number | boolean,
): string | number | boolean {
  const definition = definitions.find((item) => item.key === field)
  if (definition?.defaultValue != null) {
    return definition.defaultValue as string | number | boolean
  }
  return fallback ?? ''
}

export function getClearedTestFieldValue(
  field: string,
  fallback?: number,
): string | number | boolean {
  if (field === 'applicant_count') return 1
  if (field === 'past_customer') return false
  if (field === 'applicant_id') return fallback ?? ''
  return ''
}

export function buildApplicantTestRow(
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
  defaultApplicantId: number,
): ApplicantFormRow {
  const row: ApplicantFormRow = {}
  applicantParamKeys.forEach((field) => {
    if (!paramSelection[field]) return
    row[field] = getDefaultTestFieldValue(
      field,
      definitions,
      field === 'applicant_id' ? defaultApplicantId : undefined,
    )
  })
  return row
}

export function buildInitialTestFormValues(
  applicationParamKeys: string[],
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): ModelTestFormValues {
  const values: ModelTestFormValues = {}

  applicationParamKeys.forEach((field) => {
    if (!paramSelection[field]) return
    if (field === 'applicant_count') {
      values[field] = 1
      return
    }
    if (field === 'past_customer') {
      values[field] = false
      return
    }
    values[field] = getDefaultTestFieldValue(field, definitions)
  })

  const hasApplicantFields = applicantParamKeys.some((field) => paramSelection[field])
  if (hasApplicantFields) {
    values.applicants = [buildApplicantTestRow(applicantParamKeys, paramSelection, definitions, 1)]
  }

  return values
}

export function getApplicantCountFromForm(
  paramSelection: ModelCalculationParamSelection,
  formValues: ModelTestFormValues,
): number {
  if (!paramSelection.applicant_count) return 1
  return Math.max(1, Number(formValues.applicant_count) || 1)
}

export function syncApplicantRows(
  formValues: ModelTestFormValues,
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): ModelTestFormValues {
  const hasApplicantFields = applicantParamKeys.some((field) => paramSelection[field])
  if (!hasApplicantFields) return formValues

  const targetCount = getApplicantCountFromForm(paramSelection, formValues)
  const current = Array.isArray(formValues.applicants) ? [...formValues.applicants] : []

  while (current.length < targetCount) {
    current.push(
      buildApplicantTestRow(applicantParamKeys, paramSelection, definitions, current.length + 1),
    )
  }
  while (current.length > targetCount) {
    current.pop()
  }

  return { ...formValues, applicants: current }
}

export function getExpectedBulkValueCount(
  applicationParamKeys: string[],
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  applicantCount: number,
): number {
  let count = 0
  applicationParamKeys.forEach((field) => {
    if (paramSelection[field]) count++
  })

  const hasApplicantFields = applicantParamKeys.some((field) => paramSelection[field])
  if (hasApplicantFields) {
    const applicantFieldCount = applicantParamKeys.filter((field) => paramSelection[field]).length
    count += applicantCount * applicantFieldCount
  }

  return count
}

export function parseBulkValues(raw: string): string[] {
  return String(raw || '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value !== '')
}

export function validateBulkValues(raw: string, expectedCount: number): string {
  const trimmed = String(raw || '').trim()
  if (!trimmed) return 'Enter comma-separated values to insert.'
  if (/[;|]/.test(trimmed) || /,\s*,/.test(trimmed)) {
    return 'Enter values in comma-separated format only (e.g. value1, value2, value3).'
  }
  const values = parseBulkValues(trimmed)
  if (!values.length) {
    return 'Enter values in comma-separated format only (e.g. value1, value2, value3).'
  }
  if (trimmed.indexOf(',') === -1 && expectedCount > 1) {
    return 'Enter values in comma-separated format only (e.g. value1, value2, value3).'
  }
  return ''
}

export function coerceBulkTestValue(
  field: string,
  value: string,
  definitions: ModelParamDefinition[],
): string | number | boolean {
  if (field === 'applicant_count') {
    const count = Number(value)
    return count > 0 ? count : 1
  }
  if (field === 'past_customer') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  if (getParamInputType(field, definitions) === 'number') {
    const numeric = Number(value)
    return value !== '' && !Number.isNaN(numeric) ? numeric : value
  }
  return value
}

export function applyBulkValuesToForm(
  raw: string,
  formValues: ModelTestFormValues,
  applicationParamKeys: string[],
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
): { next: ModelTestFormValues; error: string } {
  const applicantCountBefore = getApplicantCountFromForm(paramSelection, formValues)
  const expectedCount = getExpectedBulkValueCount(
    applicationParamKeys,
    applicantParamKeys,
    paramSelection,
    applicantCountBefore,
  )

  const validationError = validateBulkValues(raw, expectedCount)
  if (validationError) {
    return { next: formValues, error: validationError }
  }

  const values = parseBulkValues(raw)
  const next: ModelTestFormValues = { ...formValues, applicants: [...(formValues.applicants ?? [])] }
  let valueIndex = 0

  applicationParamKeys.forEach((field) => {
    if (!paramSelection[field] || valueIndex >= values.length) return
    next[field] = coerceBulkTestValue(field, values[valueIndex]!, definitions)
    valueIndex++
  })

  const synced = syncApplicantRows(next, applicantParamKeys, paramSelection, definitions)
  const applicantCountAfter = getApplicantCountFromForm(paramSelection, synced)
  const finalExpectedCount = getExpectedBulkValueCount(
    applicationParamKeys,
    applicantParamKeys,
    paramSelection,
    applicantCountAfter,
  )

  if (values.length !== finalExpectedCount) {
    return {
      next: formValues,
      error: `Inserted ${Math.min(values.length, finalExpectedCount)} of ${finalExpectedCount} field${finalExpectedCount === 1 ? '' : 's'}. Provide exactly ${finalExpectedCount} comma-separated value${finalExpectedCount === 1 ? '' : 's'}.`,
    }
  }

  if (Array.isArray(synced.applicants)) {
    synced.applicants = synced.applicants.map((applicant) => {
      const row = { ...applicant }
      applicantParamKeys.forEach((field) => {
        if (!paramSelection[field] || valueIndex >= values.length) return
        row[field] = coerceBulkTestValue(field, values[valueIndex]!, definitions)
        valueIndex++
      })
      return row
    })
  }

  return { next: synced, error: '' }
}

export function clearTestFormValues(
  applicationParamKeys: string[],
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
  definitions: ModelParamDefinition[],
  formValues: ModelTestFormValues,
): ModelTestFormValues {
  const next: ModelTestFormValues = { ...formValues }

  applicationParamKeys.forEach((field) => {
    if (!paramSelection[field]) return
    next[field] = getClearedTestFieldValue(field)
  })

  const synced = syncApplicantRows(next, applicantParamKeys, paramSelection, definitions)
  if (Array.isArray(synced.applicants)) {
    synced.applicants = synced.applicants.map((applicant, index) => {
      const row = { ...applicant }
      applicantParamKeys.forEach((field) => {
        if (!paramSelection[field]) return
        row[field] = getClearedTestFieldValue(field, field === 'applicant_id' ? index + 1 : undefined)
      })
      return row
    })
  }

  return synced
}

export function isTestFormValid(
  formValues: ModelTestFormValues,
  applicationParamKeys: string[],
  applicantParamKeys: string[],
  paramSelection: ModelCalculationParamSelection,
): boolean {
  for (const field of applicationParamKeys) {
    if (!paramSelection[field]) continue
    if (field === 'past_customer') continue
    const value = formValues[field]
    if (value === '' || value == null) return false
  }

  if (Array.isArray(formValues.applicants)) {
    for (const applicant of formValues.applicants) {
      for (const field of applicantParamKeys) {
        if (!paramSelection[field]) continue
        if (field === 'past_customer') continue
        const value = applicant[field]
        if (value === '' || value == null) return false
      }
    }
  }

  return true
}
